import { getLegacyBridge } from "./state";

const IMAGE_EDITOR_SCRIPT_VERSION = "runtime-420";
const IMAGE_EDITOR_SCRIPT_SRC = `/static/image-editor.js?v=${IMAGE_EDITOR_SCRIPT_VERSION}`;

let imageEditorLoadPromise: Promise<void> | null = null;

function legacyMethod(name: string, ...args: any[]) {
  return getLegacyBridge().methods[name]?.(...args);
}

function setStatus(message: string, type = "") {
  legacyMethod("setStatus", message, type);
}

function currentImageEditorMethod(name: string) {
  const method = getLegacyBridge().methods[name];
  if (method === openImageEditor || method === closeImageEditor || method === handleImageEditorHistoryShortcut || method === isImageEditorModalOpen) {
    return null;
  }
  return typeof method === "function" ? method : null;
}

function isEditableImageSource(source: any) {
  if (!source || source.missing) return false;
  if (source.kind === "upload") return Boolean(source.file);
  return ["gallery", "asset"].includes(source.kind) && Boolean(legacyMethod("sourcePreviewUrl", source));
}

function loadImageEditorFeature() {
  if (currentImageEditorMethod("openImageEditor")) return Promise.resolve();
  if (imageEditorLoadPromise) return imageEditorLoadPromise;

  imageEditorLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-agentra-image-editor="true"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Image editor script failed to load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = IMAGE_EDITOR_SCRIPT_SRC;
    script.async = true;
    script.dataset.agentraImageEditor = "true";
    script.addEventListener("load", () => {
      try {
        window.__agentraImageEditorFeatureReady?.();
        resolve();
      } catch (error) {
        reject(error);
      }
    }, { once: true });
    script.addEventListener("error", () => {
      script.remove();
      reject(new Error("Image editor script failed to load"));
    }, { once: true });
    document.head.append(script);
  }).catch((error) => {
    imageEditorLoadPromise = null;
    throw error;
  });

  return imageEditorLoadPromise;
}

async function openImageEditor(index: any) {
  if (!isEditableImageSource(getLegacyBridge().state.images[index])) {
    setStatus("Ảnh này chưa thể chỉnh sửa.", "error");
    return;
  }

  setStatus("Đang tải trình chỉnh sửa ảnh...", "ok");
  try {
    await loadImageEditorFeature();
    const method = currentImageEditorMethod("openImageEditor");
    if (!method) throw new Error("Image editor did not register");
    return method(index);
  } catch {
    setStatus("Không tải được trình chỉnh sửa ảnh. Vui lòng thử lại.", "error");
  }
}

function closeImageEditor() {
  const method = currentImageEditorMethod("closeImageEditor");
  method?.();
}

function isImageEditorModalOpen() {
  const method = currentImageEditorMethod("isImageEditorModalOpen");
  return Boolean(method?.());
}

function handleImageEditorHistoryShortcut(event: KeyboardEvent) {
  const method = currentImageEditorMethod("handleImageEditorHistoryShortcut");
  return Boolean(method?.(event));
}

export function initImageEditorLazyFeature() {
  Object.assign(getLegacyBridge().methods, {
    openImageEditor,
    closeImageEditor,
    isEditableImageSource,
    handleImageEditorHistoryShortcut,
    isImageEditorModalOpen,
  });
}
